<template>
   <ModalFormTemplate
    title="Shift End Report"
    width="50rem"
    end-point=""
    icon="clock"
    :ignore-permission="true"
    :show-save="false"
    :show-save-close="false"
    @save="runReport"
    mode="create"
    @close="handleClose"
  >
    <template #header>
      <Buttons
        caption="Print A4 Report"
        icon="Printer"
        type="success"
        v-if="!shiftEnded"
        @click="runReport(false)"
        id="button-print-report"
      />
      <Buttons
        caption="Print Thermal Report"
        icon="ReceiptText"
        v-if="!shiftEnded"
        type="success"
        @click="runReport(true)"
        id="button-print-report"
      />
    </template>
    <template #form>
    <div class="gap-6">
      <div class="p-6 overflow-y-auto flex-1">
        <!-- Report Header -->
        <div class="mb-6 grid grid-cols-1 gap-4">
          <div class="p-6">
          <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Shift Details
          </h3>
          <div class="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-6">
            <div class="space-y-2">
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">Outlet:</span>
                <span class="font-medium">{{
                  posStation?.outlet.name
                }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">POS Station:</span>
                <span class="font-medium">{{
                  posStation?.name
                }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">User:</span>
                <span class="font-medium">{{
                  tokenManager.getUser()?.name
                }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">Started On:</span>
                <span class="font-medium">{{
                  formatDate(cashierDuty?.started_time, "yyyy-mm-dd HH:MM")
                }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400"
                  >Float Amount:</span
                >
                <span class="font-medium">{{
                  cashierDuty?.float.reduce((acc, den) => acc + den.amount, 0)
                }}</span>
              </div>
            </div>
          </div>
        </div>
        </div>

        

         <!-- Sales Summary -->
         <div class="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-6">
          <h4 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Sales Summary</h4>
          <div class="space-y-2">
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Total Sales</span>
              <span class="font-medium">{{ formatCurrency(shiftEndReport.sales.sub_total) }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Total Transactions</span>
              <span class="font-medium">{{ shiftEndReport.sales.invoice_count }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Cancelled Transactions</span>
              <span class="font-medium text-red-600">{{ shiftEndReport.cancelled }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Discounts</span>
              <span class="font-medium text-red-600">-{{ formatCurrency(shiftEndReport.sales.discount) }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Refunds</span>
              <span class="font-medium text-red-600">-{{ formatCurrency(shiftEndReport.refunds) }}</span>
            </div>
            <div class="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
              <span class="font-medium text-gray-900 dark:text-white">Total Tax</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ formatCurrency(shiftEndReport.sales.tax) }}</span>
            </div>
            <div class="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
              <span class="font-medium text-gray-900 dark:text-white">Net Sales</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ formatCurrency(shiftEndReport.sales.net_total) }}</span>
            </div>
            <div class="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
              <span class="font-medium text-gray-900 dark:text-white">Total Credit Sales</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ formatCurrency(totalCreditSales) }}</span>
            </div>
            <div class="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
              <span class="font-medium text-gray-900 dark:text-white">Total Payment Receipts</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ formatCurrency(totalPaymentReceipts) }}</span>
            </div>
            <div class="flex justify-between pt-2 pb-2 border-t border-gray-200 dark:border-gray-900 bg-gray-300 dark:bg-gray-700">
              <span class="font-medium text-gray-900 dark:text-white">Total Collection</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ formatCurrency(shiftEndReport.sales.net_total - totalCreditSales + totalPaymentReceipts) }}</span>
            </div>
          </div>
        </div>

        <!-- Payment Details Grid -->
        <div class="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">

          <!-- Grid Header -->
          <div
            class="grid grid-cols-4 gap-4 mb-2 font-medium text-gray-700 dark:text-gray-300 text-sm"
          >
            <div>Payment Type</div>
            <div class="text-right">Actual Sales</div>
            <div class="text-right">Counted Amount</div>
            <div class="text-right">Difference</div>
          </div>

          <hr class="border-gray-200 dark:border-gray-700 mb-2" />

          <!-- Grid Body -->
          <div class="space-y-2">
            <div
              v-for="payment in shiftEndReport.payment_types"
              :key="payment.id"
              class="grid grid-cols-4 gap-4 items-center py-2 border-b border-gray-200 dark:border-gray-700"
            >
              <div class="font-medium text-gray-900 dark:text-white">
                {{ payment.payment_type }}
              </div>
              <div class="text-right text-gray-900 dark:text-white">
                {{ formatCurrency(Number(payment.amount || 0)) }}
              </div>
              <div class="flex items-center gap-2">
                <input
                  v-model="payment.actual_count"
                  type="number"
                  step="0.01"
                  :readonly="toBool(payment.has_denomination)"
                  class="w-full px-3 py-2 text-right rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  :class="{
                    'bg-gray-50 dark:bg-gray-800': toBool(payment.has_denomination),
                  }"
                />
                <button
                  v-if="toBool(payment.has_denomination)"
                  @click="handleCount(payment)"
                  class="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Count
                </button>
              </div>
              <div
                class="text-right font-medium"
                :class="
                  getDifference(Number(payment.amount || 0), Number(payment.actual_count || 0)) >= 0
                    ? 'text-green-600'
                    : 'text-red-600'
                "
              >
                {{
                  formatCurrency(
                    getDifference(Number(payment.amount || 0), Number(payment.actual_count || 0))
                  )
                }}
              </div>
            </div>

            <!-- Total Row -->
            <div class="grid grid-cols-4 gap-4 items-center pt-2 font-medium">
              <div class="text-gray-900 dark:text-white">Total</div>
              <div class="text-right text-gray-900 dark:text-white">
                {{ formatCurrency(totalSystem) }}
              </div>
              <div class="text-right text-gray-900 dark:text-white">
                {{formatCurrency(totalActual) }}
              </div>
              <div
                class="text-right"
                :class="
                  totalDifference >= 0 ? 'text-green-600' : 'text-red-600'
                "
              >
                {{ formatCurrency(totalDifference) }}
              </div>
            </div>
          </div>
        </div>

        <div class="mt-5"></div>        
        <div class="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-6">
          <h4 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Credit Invoices</h4>
        <!-- Grid Header -->
        <div
          class="grid grid-cols-4 gap-4 mb-2 font-medium text-gray-700 dark:text-gray-300 text-sm"
        >
          <div>Customer</div>
          <div class="text-left">Invoice No</div>
          <div class="text-right">Amount</div>
        </div>

        <hr class="border-gray-200 dark:border-gray-700 mb-2" />

        <!-- Grid Body -->
        <div class="space-y-2">
          <div
            v-for="invoice in shiftEndReport.credit_sales"
            :key="invoice.document_no"
            class="grid grid-cols-4 gap-4 items-center py-2 border-b border-gray-200 dark:border-gray-700"
          >
            <div class="font-medium text-gray-900 dark:text-white">
              {{ invoice.customer }}
            </div>
            <div class="font-medium text-gray-900 dark:text-white">
              {{ invoice.document_no }}
            </div>
            <div class="text-right text-gray-900 dark:text-white">
              {{ formatCurrency(Number(invoice.net_total || 0)) }}
            </div>
          </div>

          <!-- Total Row -->
          <div class="grid grid-cols-4 gap-4 items-center pt-2 pb-2 font-medium bg-gray-300 dark:bg-gray-700">
            <div class="text-gray-900 dark:text-white">Total</div>
            <div class="text-right text-gray-900 dark:text-white">
              
            </div>
            <div class="text-right text-gray-900 dark:text-white">
              {{formatCurrency(totalCreditSales) }}
            </div>
          </div>
        </div>
        </div>


        <div class="mt-5"></div>        
        <div class="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-6">
          <h4 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Payment Receipts</h4>
        <!-- Grid Header -->
        <div
          class="grid grid-cols-5 gap-4 mb-2 font-medium text-gray-700 dark:text-gray-300 text-sm"
        >
          <div>Customer</div>
          <div class="text-left">Invoice No</div>
          <div class="text-right">Invoice Amount</div>
          <div class="text-left">Receipt No</div>
          <div class="text-right">Payment Amount</div>
        </div>

        <hr class="border-gray-200 dark:border-gray-700 mb-2" />

        <!-- Grid Body -->
        <div class="space-y-2">
          <div
            v-for="invoice in shiftEndReport.payment_receipts"
            :key="invoice.document_no"
            class="grid grid-cols-5 gap-4 items-center py-2 border-b border-gray-200 dark:border-gray-700"
          >
            <div class="font-medium text-gray-900 dark:text-white">
              {{ invoice.customer }}
            </div>
            <div class="font-medium text-gray-900 dark:text-white">
              {{ invoice.invoice_no }}
            </div>
            <div class="text-right text-gray-900 dark:text-white">
              {{ formatCurrency(Number(invoice.invoice_amount || 0)) }}
            </div>
            <div class="font-medium text-gray-900 dark:text-white">
              {{ invoice.payment_receipt_no }}
            </div>
            <div class="text-right text-gray-900 dark:text-white">
              {{ formatCurrency(Number(invoice.amount || 0)) }}
            </div>
          </div>

          <!-- Total Row -->
          <div class="grid grid-cols-4 gap-4 items-center pt-2 pb-2 font-medium bg-gray-300 dark:bg-gray-700">
            <div class="text-gray-900 dark:text-white">Total</div>
            <div class="text-right text-gray-900 dark:text-white">
              
            </div>
            <div class="text-right text-gray-900 dark:text-white">
              {{formatCurrency(totalPaymentReceipts) }}
            </div>
          </div>
        </div>
        </div>

      </div>
    </div>
    <DenominationCountModal
      :payment-type="selectedType"
      v-if="showDenominationCount"
      @update="handleDenominationUpdate"
      @close="showDenominationCount = false"
    />
    </template>
    
  </ModalFormTemplate>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import DenominationCountModal from "./DenominationCountModal.vue";
import { tokenManager } from "../../../../../api/utils/tokenManager";
import { api } from "../../../../../api/Api";
import { toBool, formatDate, printPDFBlob } from "../../../../../utils/CommonFunctions";
import { CashierDuty, PaymentReceipts, PaymentType, PosStation } from "../../../../../types/models";
import Buttons from "../../../../../components/ui/form/Buttons.vue";
import ModalFormTemplate from '../../../../../components/ui/Templates/ModalFormTemplate.vue';
import MessageBox from "../../../../../services/MessageBox";

const emit = defineEmits<{
  (e: "close"): void;
  (e: "complete"): void;
}>();

const props = defineProps<{
  endPoint: string;
  posStation: PosStation | undefined
  cashierDuty: CashierDuty | null
}>();


interface ShiftEndReport {
  cancelled: number;
  refunds: number;
  payment_types: PaymentType[];
  sales: SalesSummary;
  credit_sales: SalesSummary[];
  payment_receipts: PaymentReceipts[];
}

interface SalesSummary {
  sub_total: number;
  discount: number;
  tax: number;
  net_total: number;
  invoice_count: number;
  document_no?: string | number
  customer?: string
};


const shiftEndReport = ref<ShiftEndReport>({
  cancelled: 0,
  refunds: 0,
  payment_types: [],
  credit_sales: [],
  payment_receipts: [],
  sales: {
    sub_total: 0,
    discount: 0,
    tax: 0,
    net_total: 0,
    invoice_count: 0,
  },
}); 
//const endPoint = 'transactions/front-office/memos/'

const showDenominationCount = ref(false);

//const shiftEndReportPaymentTypes = ref<PaymentType[]>([]);


const handleCount = (payment: PaymentType) => {
  console.log("payment", payment);
  selectedType.value = payment;
  showDenominationCount.value = true;
};

const selectedType = ref<PaymentType>();

/*const handlePrint = () => {
  if (totalDifference.value != 0 ) {
    MessageBox.confirm({
      title: "Shift End Report",
      message: "Theres a difference between the Actual and the Counted amount, are you sure you want to proceed?.",
      type: "warning",
      confirmText: "Yes",
      cancelText: "No",
    }).then((result: any) => {
      if (result) {
        runReport();
      }
    });
  } else {
    runReport();
  }
};*/

const shiftEnded = ref(false);
const runReport = async (thermal = false)  => {
  MessageBox.confirm({
        title: 'Confirm Shift End',
        message: 'Are you sure you want to End the Shift? <br />This action is irreversible', 
        confirmText: 'Yes, End Shift',
        cancelText: "Cancel",
      }).then((confirmed: any) => {
        if (confirmed) {
          postRunReport(thermal)
        }
      });
}

const postRunReport = async (thermal = false) => {
  const response = await api.post(
    (props.endPoint.slice(-1) == "/" ? props.endPoint : props.endPoint + "/") +
      "end-shift",
    {
      //duty_rosters_id: myInfo.value?.duty?.id,
        details: shiftEndReport.value.sales,
        thermal: thermal,
        payment_types: shiftEndReport.value.payment_types,
        system: totalSystem.value, //getTotalSystem(),
        actual: totalActual.value, //getTotalActual(),
        difference: totalDifference.value, //getTotalDifference(),
        outlets_id: props.posStation?.outlet?.id,
        pos_stations_id: props.posStation?.id,
        cashier_dutys_id: props.cashierDuty?.id,
        //workstations_id: myInfo?.value?.workstation?.id,
        //facilitys_id: myInfo.value?.service_location?.facilitys_id,
        //facility_service_locations_id: myInfo.value?.service_location?.id,
    }
  );
  if (response.success == true) {
    shiftEnded.value = true;
    //printPDF(response.data)
    printPDFBlob(response.data);    
  }
}

const handleClose = () => {
  if (shiftEnded.value) {
    emit("complete");
    emit("close");
  } else {
    emit("close");
  }
};  

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "MVR",
  }).format(amount);
};

const getDifference = (actual: number, counted: number) => {
  return counted - actual;
};


const totalDifference = computed(() => {
  return totalActual.value - totalSystem.value;
});

const totalCreditSales = computed(() => {
  return shiftEndReport.value.credit_sales.reduce((sum, sales) => {
    return sum + Number(sales.net_total || 0);
  }, 0);
});

const totalPaymentReceipts = computed(() => {
  return shiftEndReport.value.payment_receipts.reduce((sum, payment) => {
    return sum + Number(payment.amount || 0);
  }, 0);
});

const totalActual = computed(() => {
  return shiftEndReport.value.payment_types.reduce((sum, payment) => {
    return sum + Number(payment.actual_count || 0);
  }, 0);
});

const totalSystem = computed(() => {
  return shiftEndReport.value.payment_types.reduce((sum, payment) => {
    return sum + Number(payment.amount || 0);
  }, 0);
});

const handleDenominationUpdate = (total: number) => {
  if (selectedType.value) {
    selectedType.value.actual_count = total;
  }
};

const handleGetShiftReport = async () => {
  const response = await api.post(
    (props.endPoint.slice(-1) == "/" ? props.endPoint : props.endPoint + "/") +
      "shift-end-report",
    {
      outlets_id: props.posStation?.outlet?.id,
      pos_stations_id: props.posStation?.id,
      cashier_dutys_id: props.cashierDuty?.id,
    }
  );
  if (response.success == true) {
    shiftEndReport.value = response.data;
    console.log(shiftEndReport.value);
  }
}

onMounted(() => {
  handleGetShiftReport();
  console.log(props.cashierDuty);
});

</script>
